import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Paintbrush, ArrowLeft, Send, MessageSquare, User, Clock, FileText, Image, Scissors, Palette } from "lucide-react";
import { toast } from "react-toastify";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DesignsList() {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const fetchDesigns = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/designs/all`, { withCredentials: true });
      setDesigns(data.designs || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDesigns(); }, [fetchDesigns]);

  const openDesign = async (design) => {
    setSelected(design);
    try {
      const { data } = await axios.get(`${API}/designs/${design.design_number}/messages`);
      setMessages(data.messages || []);
    } catch { setMessages([]); }
  };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendReply = async () => {
    if (!msgText.trim() || !selected) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/designs/messages`, {
        design_number: selected.design_number,
        sender: "staff",
        sender_name: "Fit Print Bros Team",
        message: msgText.trim(),
      });
      setMessages(prev => [...prev, data.data]);
      setMsgText("");
    } catch { toast.error("Failed to send"); }
    finally { setSending(false); }
  };

  const methodIcon = (m) => {
    if (m?.includes("embroidery")) return <Scissors className="w-3.5 h-3.5 text-amber-400" />;
    if (m?.includes("screen")) return <Palette className="w-3.5 h-3.5 text-blue-400" />;
    return <Palette className="w-3.5 h-3.5 text-orange-400" />;
  };

  /* ═══ DETAIL VIEW ═══ */
  if (selected) {
    return (
      <div className="space-y-4" data-testid="design-detail">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => setSelected(null)} className="text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h2 className="text-xl font-bold text-white">{selected.design_number}</h2>
          <Badge className="bg-orange-500/20 text-orange-400 text-xs">{selected.print_method || selected.product_id || "—"}</Badge>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* Design info */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 space-y-2">
              <h3 className="text-sm font-medium text-zinc-400 flex items-center gap-2"><FileText className="w-4 h-4" /> Design Details</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-zinc-500">Product</span><span className="text-white">{selected.product_name}</span></div>
                {selected.garment_type && <div className="flex justify-between"><span className="text-zinc-500">Garment</span><span className="text-white">{selected.garment_type}</span></div>}
                {selected.garment_color && <div className="flex justify-between"><span className="text-zinc-500">Color</span><span className="text-white">{selected.garment_color}</span></div>}
                {selected.size && <div className="flex justify-between"><span className="text-zinc-500">Size</span><span className="text-white">{selected.size}</span></div>}
                <div className="flex justify-between"><span className="text-zinc-500">Placement</span><span className="text-white">{selected.placement}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Quantity</span><span className="text-white">{selected.quantity}</span></div>
                {selected.embroidery_width > 0 && <div className="flex justify-between"><span className="text-zinc-500">Embroidery Size</span><span className="text-white">{selected.embroidery_width}" x {selected.embroidery_height}"</span></div>}
              </div>
            </CardContent>
          </Card>

          {/* Contact info */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 space-y-2">
              <h3 className="text-sm font-medium text-zinc-400 flex items-center gap-2"><User className="w-4 h-4" /> Contact</h3>
              <div className="space-y-1 text-sm">
                {selected.customer_name && <div className="flex justify-between"><span className="text-zinc-500">Name</span><span className="text-white">{selected.customer_name}</span></div>}
                {selected.customer_email && <div className="flex justify-between"><span className="text-zinc-500">Email</span><span className="text-white">{selected.customer_email}</span></div>}
                {selected.customer_phone && <div className="flex justify-between"><span className="text-zinc-500">Phone</span><span className="text-white">{selected.customer_phone}</span></div>}
                <div className="flex justify-between"><span className="text-zinc-500">Created</span><span className="text-white">{new Date(selected.created_at).toLocaleDateString()}</span></div>
              </div>
            </CardContent>
          </Card>

          {/* Artwork + Notes */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 space-y-2">
              <h3 className="text-sm font-medium text-zinc-400 flex items-center gap-2"><Image className="w-4 h-4" /> Artwork</h3>
              {selected.artwork_url ? (
                <img src={selected.artwork_url} alt="Design artwork" className="w-full h-32 object-contain bg-zinc-800 rounded-lg" />
              ) : (
                <div className="bg-zinc-800 rounded-lg h-32 flex items-center justify-center text-zinc-600 text-sm">No artwork uploaded</div>
              )}
              {selected.notes && (
                <div className="mt-2">
                  <p className="text-zinc-500 text-xs font-medium">Customer Notes:</p>
                  <p className="text-zinc-300 text-sm mt-1 whitespace-pre-wrap">{selected.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Messages thread */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-zinc-400 flex items-center gap-2 mb-3"><MessageSquare className="w-4 h-4" /> Conversation</h3>
            <div className="space-y-2 max-h-[300px] overflow-y-auto mb-3 pr-1">
              {messages.length === 0 && <p className="text-zinc-600 text-sm text-center py-6">No messages yet. Start the conversation.</p>}
              {messages.map(m => (
                <div key={m.id} className={`flex ${m.sender === "staff" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-xl px-3 py-2 ${m.sender === "staff" ? "bg-orange-500/20 text-orange-200" : "bg-zinc-800 text-zinc-200"}`}>
                    <p className="text-sm">{m.message}</p>
                    <p className="text-[9px] text-zinc-500 mt-0.5">{m.sender_name} &middot; {new Date(m.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            <div className="flex gap-2">
              <Input value={msgText} onChange={e => setMsgText(e.target.value)} placeholder="Reply to customer..."
                className="bg-zinc-800 border-zinc-700 text-white" onKeyDown={e => e.key === "Enter" && sendReply()} data-testid="staff-reply-input" />
              <Button onClick={sendReply} disabled={sending} className="bg-gradient-to-r from-orange-500 to-red-600 text-white shrink-0" data-testid="staff-reply-send">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ═══ LIST VIEW ═══ */
  return (
    <div className="space-y-4" data-testid="designs-list">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
          <Paintbrush className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400" /> Design Submissions
        </h2>
        <Badge className="bg-zinc-800 text-zinc-400">{designs.length} designs</Badge>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-center py-12">Loading...</div>
      ) : designs.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="py-12 text-center">
            <Paintbrush className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400">No design submissions yet.</p>
            <p className="text-zinc-600 text-sm mt-1">Designs created in the Design Studio will appear here.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {designs.map(d => (
            <Card key={d.design_number} className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition cursor-pointer" onClick={() => openDesign(d)}>
              <CardContent className="p-4">
                <div className="flex items-start sm:items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center shrink-0">
                    {methodIcon(d.print_method || d.product_id)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-white font-medium">{d.design_number}</span>
                      <Badge className="bg-zinc-800 text-zinc-400 text-[10px]">{d.product_name}</Badge>
                      {d.notes && <MessageSquare className="w-3 h-3 text-orange-400" title="Has notes" />}
                      {(d.messages?.length || 0) > 0 && <Badge className="bg-blue-500/20 text-blue-400 text-[10px]">{d.messages.length} msg</Badge>}
                    </div>
                    <p className="text-zinc-500 text-sm truncate">
                      {d.customer_name || d.customer_email || "Anonymous"}{d.garment_color ? ` — ${d.garment_color} ${d.garment_type}` : ""}
                      {d.quantity > 1 ? ` — ${d.quantity} pcs` : ""}
                    </p>
                    {d.notes && <p className="text-zinc-600 text-xs truncate mt-0.5">{d.notes}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-zinc-500 flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(d.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
